import React from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { GeneralDialog } from 'sharedComponents';
import { formatDateTime } from 'helpers/dateTimeFormatter';
import LoadingStatusWrapper from 'sharedComponents/WithLoadingStatus/LoadingStatusWrapper';
import { requestStatuses, responseStatuses } from '../../../constants';
import { getRequestStatusText, getResponseStatusText, isRequestStatusRequested } from '../helpers';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { useReportIssue } from 'sharedHooks/useReportIssue';
import { apiCallStatus } from 'helpers/constants';
import './style.scss';
import { sensorLastCommandStatus, dcuGeneration } from '../constants';

const LoadMeasurementComponent = ({
    model: {
        loadingStatuses,
        onDemandDataCollectionRequest,
        onDemandDataCollectionResponseMessage,
        onDemandDataCollectionData,
        sensorGatewayInfo,
        errorData,
        loadMeasurementErrorData,
    },
    actions: {
        loadMeasurement
    },
    show,
    close,
    onRetry,

}) => {
    const { t: translate } = useTranslation();
    const { showReportIssue } = useReportIssue();
    const DcuGenerationProperty = sensorGatewayInfo?.data?.dcuGeneration;
    const isDcuGeneration = DcuGenerationProperty === dcuGeneration.DCUGENERATION_PROPERTY;

    const renderRequestStatusTime = () => {
        if (isDcuGeneration) {
            return isRequestStatusRequested(onDemandDataCollectionRequest?.featureValue) &&
                <Col>{formatDateTime(onDemandDataCollectionRequest?.timeStamp)}</Col>;
        }
        return onDemandDataCollectionData?.commandStatus &&
            <Col>{formatDateTime(onDemandDataCollectionData?.requestTime)}</Col>;
    };

    const renderErrorReason = () => {
        if (isDcuGeneration) {
            if (onDemandDataCollectionResponseMessage?.featureValue !== null &&
                onDemandDataCollectionResponseMessage?.featureValue !== responseStatuses.COMPLETED_SUCCESSFULLY) {
                return <Row className='error-reason-row gx-0'>
                    <Col>{translate('ABB.Powertrain.Frontend.errorReasonLabel')}&nbsp;
                        <span className='error-reason-text'>
                            {onDemandDataCollectionData?.errorReason}
                        </span>
                    </Col>
                </Row>;
            }
            return null;
        }
        if (onDemandDataCollectionData?.commandStatus &&
            onDemandDataCollectionData?.commandStatus !== sensorLastCommandStatus.SUCCESS &&
            onDemandDataCollectionData?.errorReason &&
            onDemandDataCollectionResponseMessage?.featureValue !== null &&
            onDemandDataCollectionResponseMessage?.featureValue !== responseStatuses.COMPLETED_SUCCESSFULLY) {
            return <Row className='error-reason-row gx-0'>
                <Col>{translate('ABB.Powertrain.Frontend.errorReasonLabel')}&nbsp;
                    <span className='error-reason-text'>
                        {getResponseStatusText(onDemandDataCollectionResponseMessage?.featureValue)}
                    </span>
                </Col>
            </Row>;
        }
        return null;
    };

    const mainContent = errorData?.loadingStatus === apiCallStatus.ERROR
        ? <ErrorView
            errorCode={errorData.errorCode}
            onRetry={onRetry}
            onReportIssue={showReportIssue}
        />
        : <LoadingStatusWrapper
            useCustomAnchor
            transparent
            loadingStatus={loadingStatuses}>
            <Row className='info-text-row gx-0'>
                <Col>{translate('ABB.Powertrain.Frontend.loadMeasurementInfoText')}</Col>
            </Row>
            <Row className='request-status-row gx-0'>
                <Col>{translate('ABB.Powertrain.Frontend.requestStatusLabel')}&nbsp;
                    <span className='request-status-text'>
                        {isDcuGeneration ? onDemandDataCollectionRequest && getRequestStatusText(onDemandDataCollectionRequest?.featureValue) : onDemandDataCollectionData?.commandStatus}
                    </span>
                </Col>
                {renderRequestStatusTime()}
            </Row>
            {renderErrorReason()}
        </LoadingStatusWrapper>;

    return <GeneralDialog
        show={show}
        close={close}
        title={translate('ABB.Powertrain.Frontend.loadMeasurementDialogTitle')}
        dialogClassName='load-measurement-dialog'
        cancelButtonProps={{
            text: translate('ABB.Powertrain.Frontend.cancelLabel'),
        }}
        acceptButtonProps={{
            disabled: isDcuGeneration ? onDemandDataCollectionRequest?.featureValue === requestStatuses.PENDING : [sensorLastCommandStatus.PENDING, sensorLastCommandStatus.EXECUTING].includes(onDemandDataCollectionData?.commandStatus),
            text: translate('ABB.Powertrain.Frontend.requestButton'),
            onClick: () => {
                loadMeasurement().then(close).catch(() => { });
            }
        }}
        closeButton={true}
    >
        <Container fluid className='load-measurement-container gx-0'>
            {loadMeasurementErrorData?.loadingStatus === apiCallStatus.ERROR
                ? <ErrorView
                    errorCode={loadMeasurementErrorData.errorCode}
                    onRetry={() => loadMeasurement().catch(() => { })}
                    onReportIssue={showReportIssue}
                />
                : mainContent
            }
        </Container>
    </GeneralDialog>;
};

LoadMeasurementComponent.propTypes = {
    model: PropTypes.shape({
        loadingStatuses: PropTypes.arrayOf(PropTypes.string),
        onDemandDataCollectionRequest: PropTypes.shape({
            featureValue: PropTypes.number,
            timeStamp: PropTypes.string
        }),
        onDemandDataCollectionResponseMessage: PropTypes.shape({
            featureValue: PropTypes.number
        }),
        onDemandDataCollectionData: PropTypes.object,
        sensorGatewayInfo: PropTypes.object,
        errorData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            errorCode: PropTypes.string
        }),
        loadMeasurementErrorData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            errorCode: PropTypes.string
        }),
    }),
    actions: PropTypes.shape({
        loadMeasurement: PropTypes.func,
    }),
    show: PropTypes.bool,
    close: PropTypes.func,
    onRetry: PropTypes.func,
};

export default LoadMeasurementComponent;
